import { DndContext, MouseSensor, pointerWithin, TouchSensor, useDraggable, useSensor, useSensors } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import AddIcon from '@mui/icons-material/Add';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Grid, Typography } from '@mui/material';
import { useSetAtom } from 'jotai';
import { nanoid } from 'nanoid';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { importModalAtom } from '@/atoms/import';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { nth } from '@/utils/arrayUtils';
import { CharactersDropableArea } from './CharactersDropableArea';

const CharacterDraggable = ({ name }: { name: string }) => {
    const { attributes, listeners, setNodeRef, transform } = useDraggable({
        id: name,
    });

    return (
        <Grid
            item
            md="auto"
            sx={{
                position: 'relative',
                cursor: 'grab',
                zIndex: '10000',
                padding: '2px 6px',
                borderRadius: '4px',
                display: 'inline-block',
                transform: CSS.Transform.toString(transform ? Object.assign(transform, { scaleY: 1, scaleX: 1 }) : null),
                background: theme => theme.palette.info.contrastText,
            }}
            ref={setNodeRef}
            {...listeners}
            {...attributes}
        >
            <Text variant="h6" color="text-white" children={name} className="uppercase" />
        </Grid>
    );
};

export const ImportScriptCharacters = ({ characters }: { characters: string[] }) => {
    const { t } = useTranslation();
    const setImportModal = useSetAtom(importModalAtom);
    const [characters_all, setCharactersAll] = useState<string[]>(characters);
    const [count, setCount] = useState([[nanoid(), nanoid()]]);

    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: {
                distance: 2,
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 150,
                tolerance: 3,
            },
        }),
    );

    return (
        <div className="grid gap-[16px]">
            <DndContext sensors={sensors} collisionDetection={pointerWithin}>
                <div className="flex flex-wrap w-full gap-[5px]">
                    {characters_all.length ? characters_all.map(item => <CharacterDraggable key={item} name={item} />) : null}
                </div>
                <div className="text-center">
                    <Typography
                        sx={{
                            color: theme => theme.custom.c42,
                        }}
                        variant="subtitle4"
                        children={t('import.characters-combine')}
                    />

                    <div className="flex w-full items-center flex-nowrap mt-[5px]">
                        <ErrorOutlineIcon
                            sx={{
                                color: theme => theme.custom.c45,
                            }}
                        />

                        <Typography
                            sx={{
                                color: theme => theme.custom.c45,
                            }}
                            lineHeight="1.428"
                            variant="body2"
                            children={t('import.info.characters-choose')}
                        />
                    </div>
                </div>
                {count.map(([id_1 = '', id_2 = '']) => (
                    <CharactersDropableArea
                        key={`${id_1}-${id_2}`}
                        id_1={id_1}
                        id_2={id_2}
                        characters={characters}
                        setCharactersAll={setCharactersAll}
                    />
                ))}

                <div className="flex flex-wrap w-full mt-[24px] justify-between items-center">
                    <Button
                        className="w-[200px] mr-4 border-none text-primary bg-secondary dark:bg-secondary disabled:bg-info-contrast dark:disabled:bg-info-contrast"
                        onClick={() =>
                            setImportModal(prev => ({
                                ...prev,
                                showCharacters: false,
                                showRules: false,
                                showStatistics: true,
                            }))
                        }
                        variant="contained"
                        children={t('buttons.combine')}
                    />

                    <Box
                        sx={{
                            cursor: 'pointer',
                        }}
                        onClick={() =>
                            setCount(prev => {
                                const lastChild = nth(prev, -1);
                                if (lastChild) return [...prev, [nanoid(), nanoid()]];
                                return prev;
                            })
                        }
                    >
                        <AddIcon sx={{ fontSize: '1rem', verticalAlign: 'middle' }} color="secondary" />
                        <Typography
                            ml="5px"
                            letterSpacing="1.3px"
                            display="inline-block"
                            variant="subtitle2"
                            color={theme => theme.custom.c36}
                            children={t('import.combine-more')}
                        />
                    </Box>
                </div>
            </DndContext>
        </div>
    );
};
